// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file padding.stylex.ts
 * @input Uses @stylexjs/stylex, spacing from theme
 * @output StyleX styles for numeric padding prop on layout components
 * @position Layout utility; used by Card, Section, and Layout sub-components
 */

import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { spacingVars } from "../theme/tokens.stylex.js";
/**
 * Maps numeric SpacingStep values to SpacingToken keys used by the container system.
 */
export const spacingStepToToken = {
  0: 'spacing0',
  0.5: 'spacing0_5',
  1: 'spacing1',
  1.5: 'spacing1_5',
  2: 'spacing2',
  3: 'spacing3',
  4: 'spacing4',
  5: 'spacing5',
  6: 'spacing6',
  8: 'spacing8',
  10: 'spacing10'
};

/**
 * Padding styles for all sides using spacing tokens.
 * Each key applies uniform padding to all four sides.
 */
export const paddingStyles = {
  "0": {
    kZCmMZ: "x18gyask",
    kwRFfy: "x1s0aq8i",
    kLKAdn: "x1ydh6w3",
    kGO01o: "x1l20ajd",
    $$css: true
  },
  "1": {
    kZCmMZ: "x1vsv5vr",
    kwRFfy: "x1nryj5t",
    kLKAdn: "xfsso4q",
    kGO01o: "xy143xn",
    $$css: true
  },
  "2": {
    kZCmMZ: "x12gdq22",
    kwRFfy: "x1djylfy",
    kLKAdn: "x1xye8es",
    kGO01o: "x1wesfrj",
    $$css: true
  },
  "3": {
    kZCmMZ: "x126nfab",
    kwRFfy: "x1t818jl",
    kLKAdn: "x1vlblms",
    kGO01o: "xvmdzux",
    $$css: true
  },
  "4": {
    kZCmMZ: "x1rey3nv",
    kwRFfy: "xnjyzlh",
    kLKAdn: "x1oa1p4a",
    kGO01o: "x1awphl8",
    $$css: true
  },
  "5": {
    kZCmMZ: "x1blguxw",
    kwRFfy: "xdbrk9v",
    kLKAdn: "xx7rijo",
    kGO01o: "x1hk98q",
    $$css: true
  },
  "6": {
    kZCmMZ: "x31w388",
    kwRFfy: "x1we12cn",
    kLKAdn: "x1adxfkp",
    kGO01o: "xjpqqx5",
    $$css: true
  },
  "8": {
    kZCmMZ: "x1j3hnjz",
    kwRFfy: "x1q91b2g",
    kLKAdn: "xoxd1wu",
    kGO01o: "x2oz4g1",
    $$css: true
  },
  "10": {
    kZCmMZ: "xqp078j",
    kwRFfy: "x160ivqr",
    kLKAdn: "xk6660b",
    kGO01o: "x2izi54",
    $$css: true
  },
  "0.5": {
    kZCmMZ: "x138rykx",
    kwRFfy: "x1le3yxw",
    kLKAdn: "xbx876j",
    kGO01o: "xij103a",
    $$css: true
  },
  "1.5": {
    kZCmMZ: "xfti1ec",
    kwRFfy: "x17hk9do",
    kLKAdn: "x1kwdpsa",
    kGO01o: "x1opdxmq",
    $$css: true
  }
};

/**
 * Container padding inline CSS variable styles for edge compensation.
 * Sets --container-padding-inline-start and --container-padding-inline-end so
 * edge-compensating children know the inline padding to compensate against.
 */
export const containerPaddingInlineVarStyles = {
  "0": {
    "--container-padding-inline-start": "x1gu2k80",
    "--container-padding-inline-end": "x91ghl5",
    $$css: true
  },
  "1": {
    "--container-padding-inline-start": "x1cvlban",
    "--container-padding-inline-end": "x2oyxnl",
    $$css: true
  },
  "2": {
    "--container-padding-inline-start": "x1xlrr2o",
    "--container-padding-inline-end": "xcas3b9",
    $$css: true
  },
  "3": {
    "--container-padding-inline-start": "xfdwxua",
    "--container-padding-inline-end": "xu0ipoa",
    $$css: true
  },
  "4": {
    "--container-padding-inline-start": "x1dlhslv",
    "--container-padding-inline-end": "xs0pscg",
    $$css: true
  },
  "5": {
    "--container-padding-inline-start": "x1s81nki",
    "--container-padding-inline-end": "xgkj7vj",
    $$css: true
  },
  "6": {
    "--container-padding-inline-start": "x1ep0dkj",
    "--container-padding-inline-end": "x94cj42",
    $$css: true
  },
  "8": {
    "--container-padding-inline-start": "xw1diwv",
    "--container-padding-inline-end": "x1b9k1pi",
    $$css: true
  },
  "10": {
    "--container-padding-inline-start": "xserb3f",
    "--container-padding-inline-end": "xx5lg5w",
    $$css: true
  },
  "0.5": {
    "--container-padding-inline-start": "x14ws0sr",
    "--container-padding-inline-end": "x1wz3t3y",
    $$css: true
  },
  "1.5": {
    "--container-padding-inline-start": "x176g23i",
    "--container-padding-inline-end": "xntetml",
    $$css: true
  }
};

/**
 * Container padding block-start/block-end CSS variable styles for vertical bleed.
 * Sets --container-padding-block-start and --container-padding-block-end so bleed children (Table, Divider, Section)
 * know the block padding to compensate against when first/last child.
 */
export const containerPaddingBlockStartVarStyles = {
  "0": {
    "--container-padding-block-start": "x1i3qcxz",
    $$css: true
  },
  "1": {
    "--container-padding-block-start": "xnsckjb",
    $$css: true
  },
  "2": {
    "--container-padding-block-start": "xa8b4fq",
    $$css: true
  },
  "3": {
    "--container-padding-block-start": "x11k4f5r",
    $$css: true
  },
  "4": {
    "--container-padding-block-start": "xm01sq8",
    $$css: true
  },
  "5": {
    "--container-padding-block-start": "xp8wdkl",
    $$css: true
  },
  "6": {
    "--container-padding-block-start": "x1hmud4d",
    $$css: true
  },
  "8": {
    "--container-padding-block-start": "xfv60at",
    $$css: true
  },
  "10": {
    "--container-padding-block-start": "x17h9kl7",
    $$css: true
  },
  "0.5": {
    "--container-padding-block-start": "xvdf9ev",
    $$css: true
  },
  "1.5": {
    "--container-padding-block-start": "x1kbx601",
    $$css: true
  }
};
export const containerPaddingBlockEndVarStyles = {
  "0": {
    "--container-padding-block-end": "xkunwnr",
    $$css: true
  },
  "1": {
    "--container-padding-block-end": "x57a7ii",
    $$css: true
  },
  "2": {
    "--container-padding-block-end": "x1lsgcmx",
    $$css: true
  },
  "3": {
    "--container-padding-block-end": "x1q3ppug",
    $$css: true
  },
  "4": {
    "--container-padding-block-end": "x4hfsld",
    $$css: true
  },
  "5": {
    "--container-padding-block-end": "xbib2ws",
    $$css: true
  },
  "6": {
    "--container-padding-block-end": "x1q8d17g",
    $$css: true
  },
  "8": {
    "--container-padding-block-end": "x8lgq76",
    $$css: true
  },
  "10": {
    "--container-padding-block-end": "x15vxphk",
    $$css: true
  },
  "0.5": {
    "--container-padding-block-end": "x1cao3zv",
    $$css: true
  },
  "1.5": {
    "--container-padding-block-end": "xv53x8y",
    $$css: true
  }
};

/**
 * Layout outer X padding CSS variable styles.
 */
export const layoutPaddingOuterXVarStyles = {
  "0": {
    "--layout-padding-outer-x": "xswhm3q",
    $$css: true
  },
  "1": {
    "--layout-padding-outer-x": "xc96xmq",
    $$css: true
  },
  "2": {
    "--layout-padding-outer-x": "x15dxnc0",
    $$css: true
  },
  "3": {
    "--layout-padding-outer-x": "xadgj3j",
    $$css: true
  },
  "4": {
    "--layout-padding-outer-x": "x1v56qcf",
    $$css: true
  },
  "5": {
    "--layout-padding-outer-x": "x1nzs0gl",
    $$css: true
  },
  "6": {
    "--layout-padding-outer-x": "x1c3n52a",
    $$css: true
  },
  "8": {
    "--layout-padding-outer-x": "x1t3kfz",
    $$css: true
  },
  "10": {
    "--layout-padding-outer-x": "x1jdf5a4",
    $$css: true
  },
  "0.5": {
    "--layout-padding-outer-x": "xihiwg7",
    $$css: true
  },
  "1.5": {
    "--layout-padding-outer-x": "x1u93lgd",
    $$css: true
  }
};

/**
 * Layout outer Y padding CSS variable styles.
 */
export const layoutPaddingOuterYVarStyles = {
  "0": {
    "--layout-padding-outer-y": "x1mzf5mb",
    $$css: true
  },
  "1": {
    "--layout-padding-outer-y": "x1gpfxoh",
    $$css: true
  },
  "2": {
    "--layout-padding-outer-y": "x10pz7y9",
    $$css: true
  },
  "3": {
    "--layout-padding-outer-y": "x1p6yq3h",
    $$css: true
  },
  "4": {
    "--layout-padding-outer-y": "xx738ci",
    $$css: true
  },
  "5": {
    "--layout-padding-outer-y": "x6yxws5",
    $$css: true
  },
  "6": {
    "--layout-padding-outer-y": "x180vrwl",
    $$css: true
  },
  "8": {
    "--layout-padding-outer-y": "xid7e43",
    $$css: true
  },
  "10": {
    "--layout-padding-outer-y": "x26l4wa",
    $$css: true
  },
  "0.5": {
    "--layout-padding-outer-y": "x1vj96e0",
    $$css: true
  },
  "1.5": {
    "--layout-padding-outer-y": "xd3dqby",
    $$css: true
  }
};

/**
 * Inline-only padding styles.
 * Use when a component needs to set inline (horizontal) padding independently
 * of block padding — e.g. the `paddingX` prop on Stack.
 */
export const paddingInlineStyles = {
  "0": {
    kZCmMZ: "x18gyask",
    kwRFfy: "x1s0aq8i",
    $$css: true
  },
  "1": {
    kZCmMZ: "x1vsv5vr",
    kwRFfy: "x1nryj5t",
    $$css: true
  },
  "2": {
    kZCmMZ: "x12gdq22",
    kwRFfy: "x1djylfy",
    $$css: true
  },
  "3": {
    kZCmMZ: "x126nfab",
    kwRFfy: "x1t818jl",
    $$css: true
  },
  "4": {
    kZCmMZ: "x1rey3nv",
    kwRFfy: "xnjyzlh",
    $$css: true
  },
  "5": {
    kZCmMZ: "x1blguxw",
    kwRFfy: "xdbrk9v",
    $$css: true
  },
  "6": {
    kZCmMZ: "x31w388",
    kwRFfy: "x1we12cn",
    $$css: true
  },
  "8": {
    kZCmMZ: "x1j3hnjz",
    kwRFfy: "x1q91b2g",
    $$css: true
  },
  "10": {
    kZCmMZ: "xqp078j",
    kwRFfy: "x160ivqr",
    $$css: true
  },
  "0.5": {
    kZCmMZ: "x138rykx",
    kwRFfy: "x1le3yxw",
    $$css: true
  },
  "1.5": {
    kZCmMZ: "xfti1ec",
    kwRFfy: "x17hk9do",
    $$css: true
  }
};

/**
 * Block-only padding override styles.
 * Use when a component needs to override block padding independently
 * of inline padding (e.g., Toolbar sets tight block padding while
 * inheriting inline padding from its container).
 */
export const paddingBlockStyles = {
  "0": {
    kLKAdn: "x1ydh6w3",
    kGO01o: "x1l20ajd",
    $$css: true
  },
  "1": {
    kLKAdn: "xfsso4q",
    kGO01o: "xy143xn",
    $$css: true
  },
  "2": {
    kLKAdn: "x1xye8es",
    kGO01o: "x1wesfrj",
    $$css: true
  },
  "3": {
    kLKAdn: "x1vlblms",
    kGO01o: "xvmdzux",
    $$css: true
  },
  "4": {
    kLKAdn: "x1oa1p4a",
    kGO01o: "x1awphl8",
    $$css: true
  },
  "5": {
    kLKAdn: "xx7rijo",
    kGO01o: "x1hk98q",
    $$css: true
  },
  "6": {
    kLKAdn: "x1adxfkp",
    kGO01o: "xjpqqx5",
    $$css: true
  },
  "8": {
    kLKAdn: "xoxd1wu",
    kGO01o: "x2oz4g1",
    $$css: true
  },
  "10": {
    kLKAdn: "xk6660b",
    kGO01o: "x2izi54",
    $$css: true
  },
  "0.5": {
    kLKAdn: "xbx876j",
    kGO01o: "xij103a",
    $$css: true
  },
  "1.5": {
    kLKAdn: "x1kwdpsa",
    kGO01o: "x1opdxmq",
    $$css: true
  }
};

/**
 * Propagation styles for --astryx-section-padding.
 * When a parent section sets explicit padding, this propagates the value
 * through the CSS custom property cascade so nested sections that use
 * useThemeDefault inherit the parent's padding instead of the theme default.
 */
export const sectionPaddingPropagationStyles = {
  "0": {
    "--astryx-section-padding": "xvdw317",
    $$css: true
  },
  "1": {
    "--astryx-section-padding": "x49wo0b",
    $$css: true
  },
  "2": {
    "--astryx-section-padding": "x1bzoqqa",
    $$css: true
  },
  "3": {
    "--astryx-section-padding": "x1nvso86",
    $$css: true
  },
  "4": {
    "--astryx-section-padding": "x1as1t6l",
    $$css: true
  },
  "5": {
    "--astryx-section-padding": "xfb5lyl",
    $$css: true
  },
  "6": {
    "--astryx-section-padding": "x1tig3w6",
    $$css: true
  },
  "8": {
    "--astryx-section-padding": "x1szikzq",
    $$css: true
  },
  "10": {
    "--astryx-section-padding": "xf1jhqy",
    $$css: true
  },
  "0.5": {
    "--astryx-section-padding": "xnvcwyz",
    $$css: true
  },
  "1.5": {
    "--astryx-section-padding": "x1rj2uvf",
    $$css: true
  }
};